import { useState, useRef, useEffect, useMemo } from "react";
import { Icons, PRODUCT_STATUSES, BOARD_CODE_TO_NAME, ROOM_TYPES_FULL, ROOM_CHARS_FULL, findSupplierService, resolveSupplierCommercialRole, isCommercialRoleFlexible } from "./bookingConstants";
import BoardCodeDropdown from "./BoardCodeDropdown";
import SearchableListDropdown from "./SearchableListDropdown";
import { useSuppliersList } from "../../api/suppliers/useSuppliersApi";
import { useGetHotels } from "../../api/products/useHotelsApi";
import s from "./HotelForm.module.css";

/* ─── Constants ─────────────────────────────────────────────────────────── */

const ROOM_STATUSES = ["Available","Confirmed","On Request","Waitlist","Cancelled"];
const EB_UNITS = ["%","EUR"];

const ROOM_RATES = ["Refundable", "Non-Refundable", "Partially Refundable"];

/* Status color classes */
const STATUS_CLS = {
  "Available":   s.statusAvailable,
  "Confirmed":   s.statusConfirmed,
  "On Request":  s.statusOnRequest,
  "Waitlist":    s.statusWaitlist,
  "Cancelled":   s.statusCancelled,
};

/* Factory for a new room */
const newRoom = () => ({
  id: `room-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
  roomRate: "Refundable",
  roomName: "",
  roomCharacteristic: "",
  roomCode: "",
  roomType: "",
  numberOfRooms: 1,
  roomStatus: "",
  boardCode: "",
  boardName: "",
});

/* ─── Hotel Search (unchanged) ──────────────────────────────────────────── */

const HotelSearchField = ({ onSelect, selectedHotel, onClear }) => {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  const { data: hotels, loading } = useGetHotels(
    debouncedQuery.length >= 2 ? { search: debouncedQuery, limit: 20 } : { limit: 0 }
  );

  const results = useMemo(
    () => (debouncedQuery.length >= 2 ? (hotels || []).map((h) => ({
      id: h.id,
      hotelCode: h.hotelCode,
      name: h.hotelName,
      stars: h.starRating || 0,
      city: h.city,
      country: h.country,
      address: h.address,
    })) : []),
    [hotels, debouncedQuery],
  );

  useEffect(() => {
    const h = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setIsOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const handleSearch = (val) => {
    setQuery(val);
    if (val.trim().length < 2) { setIsOpen(false); return; }
    setIsOpen(true);
  };
  if (selectedHotel) {
    return (
      <div className={s.hotelPickedCard}>
        <div className={s.hotelPickedCardPin}>{Icons.mapPin}</div>
        <div className={s.hotelPickedCardInfo}>
          <span className={s.hotelPickedCardName}>{selectedHotel.name}</span>
          <span className={s.hotelPickedCardStars}>{[...Array(selectedHotel.stars)].map((_,i)=><span key={i}>{Icons.starFill}</span>)}</span>
          <span className={s.hotelPickedCardLoc}>{selectedHotel.city} / {selectedHotel.country}</span>
        </div>
        <button className={s.hotelPickedCardChevron} type="button" onClick={onClear}>{Icons.chevronDown}</button>
      </div>
    );
  }
  return (
    <div className={s.hotelSearchWrap} ref={wrapperRef}>
      <div className={s.hotelSearchInputWrap}>
        <span className={s.hotelSearchIcon}>{Icons.search}</span>
        <input className={s.hotelSearchInput} placeholder="Search hotels by name, city, or country..." value={query} onChange={(e) => handleSearch(e.target.value)} onFocus={() => { if (results.length > 0) setIsOpen(true); }} />
        {query && <button className={s.hotelSearchClear} type="button" onClick={() => { setQuery(""); setIsOpen(false); }}>{Icons.x}</button>}
      </div>
      {isOpen && (
        <div className={s.hotelDropdown}>
          {loading
            ? <div className={s.hotelDropdownEmpty}>Searching...</div>
            : results.length === 0
            ? <div className={s.hotelDropdownEmpty}>No hotels found for "{query}"</div>
            : <div className={s.hotelDropdownList}>{results.map((hotel) => (
                <button key={hotel.id} className={s.hotelDropdownItem} type="button" onClick={() => { onSelect(hotel); setQuery(""); setIsOpen(false); }}>
                  <div className={s.hotelItemIcon}>{Icons.hotel}</div>
                  <div className={s.hotelItemInfo}>
                    <div className={s.hotelItemName}>{hotel.name}</div>
                    <div className={s.hotelItemMeta}>
                      <span className={s.hotelItemStars}>{[...Array(hotel.stars)].map((_,i)=><span key={i}>{Icons.starFill}</span>)}</span>
                      <span className={s.hotelItemLoc}>{Icons.mapPin} {hotel.city}, {hotel.country}</span>
                    </div>
                  </div>
                </button>))}</div>}
          <div className={s.hotelDropdownFooter}>{loading ? "..." : `${results.length} result${results.length !== 1 ? "s" : ""}`} from hotel database</div>
        </div>
      )}
    </div>
  );
};

/* ─── Stepper ───────────────────────────────────────────────────────────── */

const Stepper = ({ value, onChange, min = 0 }) => (
  <div className={s.stepperWrap}>
    <input className={`${s.input} ${s.stepperVal}`} type="number" min={min} value={value} onChange={(e) => onChange(parseInt(e.target.value) || min)} />
    <button type="button" className={s.stepperBtn} onClick={() => onChange(Math.max(min, value - 1))}>−</button>
    <button type="button" className={s.stepperBtn} onClick={() => onChange(value + 1)}>+</button>
  </div>
);

/* ─── Early-bird Row ────────────────────────────────────────────────────── */

const EBRow = ({ dateLabel, valueLabel, dateKey, unitKey, valueKey, form, f }) => (
  <div className={s.mfRow2}>
    <div className={s.field}><label className={s.fieldLabel}>{dateLabel}</label><input className={s.input} type="date" value={form[dateKey]||""} onChange={e=>f(dateKey,e.target.value)}/></div>
    <div className={s.field}><label className={s.fieldLabel}>{valueLabel}</label>
      <div className={s.ebRow}><select className={`${s.input} ${s.ebUnit}`} value={form[unitKey]||"%"} onChange={e=>f(unitKey,e.target.value)}>{EB_UNITS.map(u=><option key={u}>{u}</option>)}</select><input className={`${s.input} ${s.ebValue}`} type="number" min="0" placeholder="Enter value" value={form[valueKey]||""} onChange={e=>f(valueKey,e.target.value)}/></div>
    </div>
  </div>
);

/* ─── Icons ─────────────────────────────────────────────────────────────── */

const BedIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 4v16"/><path d="M22 4v16"/><path d="M2 8h20"/><path d="M2 16h20"/><path d="M6 8v8"/>
  </svg>
);

const CopyIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
  </svg>
);

const PlusIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="M12 5v14"/></svg>
);

const DragDotsIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="9" cy="7" r="1.5"/><circle cx="15" cy="7" r="1.5"/>
    <circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/>
    <circle cx="9" cy="17" r="1.5"/><circle cx="15" cy="17" r="1.5"/>
  </svg>
);

/* ─── Room Card ─────────────────────────────────────────────────────────── */

const ERR_BORDER = { border: "1.5px solid #ef4444", background: "#fff5f5" };
const ERR_MSG    = { fontSize: 11, color: "#ef4444", marginTop: 3 };

const RoomCard = ({ room, index, onUpdate, onRemove, onDuplicate, canRemove }) => {
  const u = (field, value) => {
    if (field === "boardCode") {
      onUpdate(room.id, { boardCode: value, boardName: BOARD_CODE_TO_NAME[value] || value });
    } else {
      onUpdate(room.id, { [field]: value });
    }
  };

  /* Inline required-field errors — shown immediately so user knows what to fill */
  const err = {
    roomStatus: !room.roomStatus,
    boardCode:  !room.boardCode,
    roomType:   !room.roomType,
  };

  return (
    <div className={s.roomCard}>
      {/* ── Header ── */}
      <div className={s.roomCardHeader}>
        <div className={s.roomIconBadge}><BedIcon /></div>
        <span className={s.roomTitle}>Room {index + 1}</span>
        {(err.roomStatus || err.boardCode || err.roomType) && (
          <span style={{ fontSize: 11, fontWeight: 600, color: "#ef4444", marginLeft: 8 }}>
            — fill required fields
          </span>
        )}
        <div className={s.roomHeaderActions}>
          <button type="button" className={s.roomActionBtn} onClick={() => onDuplicate(room.id)} title="Duplicate room">
            <CopyIcon /> Duplicate
          </button>
          {canRemove && (
            <button type="button" className={`${s.roomActionBtn} ${s.roomActionBtnDanger}`} onClick={() => onRemove(room.id)} title="Remove room">
              <TrashIcon /> Remove
            </button>
          )}
        </div>
        <div className={s.roomDragHandle}><DragDotsIcon /></div>
      </div>

      {/* ── Body ── */}
      <div className={s.roomCardBody}>
        {/* Row 1: Status + Rate */}
        <div className={s.mfRow2}>
          <div className={s.field}>
            <label className={s.fieldLabel}>Room Status <span className={s.req}>*</span></label>
            <select
              className={s.input}
              style={err.roomStatus ? ERR_BORDER : undefined}
              value={room.roomStatus || ""}
              onChange={e => u("roomStatus", e.target.value)}
            >
              <option value="">Select Status</option>
              {ROOM_STATUSES.map(st => <option key={st}>{st}</option>)}
            </select>
            {err.roomStatus && <span style={ERR_MSG}>Room Status is required</span>}
          </div>
          <div className={s.field}>
            <label className={s.fieldLabel}>Room Rate <span className={s.req}>*</span></label>
            <select className={s.input} value={room.roomRate || "Refundable"} onChange={e => u("roomRate", e.target.value)}>
              {ROOM_RATES.map(r => <option key={r}>{r}</option>)}
            </select>
          </div>
        </div>

        {/* Row 2: Board Code + Room Type + Characteristic */}
        <div className={s.mfRow3Char}>
          <div className={s.field}>
            <label className={s.fieldLabel}>Board Code <span className={s.req}>*</span></label>
            <BoardCodeDropdown value={room.boardCode || ""} onChange={(code) => u("boardCode", code)} placeholder="Select board type…" allowCustom />
          </div>
          <div className={s.field}>
            <label className={s.fieldLabel}>Room Type <span className={s.req}>*</span></label>
            <SearchableListDropdown items={ROOM_TYPES_FULL} value={room.roomType || ""} onChange={(v) => u("roomType", v)} placeholder="Select room type…" searchPlaceholder="Search room types…" allowCustom />
          </div>
          <div className={s.field}>
            <label className={s.fieldLabel}>Room Characteristic</label>
            <SearchableListDropdown items={ROOM_CHARS_FULL} value={room.roomCharacteristic || ""} onChange={(v) => u("roomCharacteristic", v)} placeholder="Select characteristic…" searchPlaceholder="Search views, features, amenities…" allowCustom />
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Main HotelForm ────────────────────────────────────────────────────── */

/* ─── Hotel commercial-role helpers (delegate to shared resolver) ─── */
// Resolved role (MERCHANT | AGENT) honouring DEFAULT → supplier-level fallback
// and BOTH → defaultCommercialRole.
const getHotelResolvedRole = (supplier) => resolveSupplierCommercialRole(supplier, "Hotel");
// True only when the hotel service is configured as BOTH — i.e. the user is
// allowed to switch between Merchant / Agent at booking time.
const isHotelRoleFlexible = (supplier) => isCommercialRoleFlexible(supplier, "Hotel");
// Raw service entry, so the UI can decide what label/affordance to render.
const getHotelService = (supplier) => findSupplierService(supplier, "Hotel");

const HotelForm = ({ form, f, initialData, supplierRefWarn }) => {
  const { data: suppliers, loading: loadingSuppliers } = useSuppliersList();
  const supplierOptions = useMemo(() =>
    (suppliers || []).filter((sup) => {
      const svc = findSupplierService(sup, "Hotel");
      return svc && !!svc.enabled;
    }),
    [suppliers]
  );
  const selectedSupplier = form.supplierId
    ? supplierOptions.find(x => String(x.id) === String(form.supplierId))
    : null;
  const hotelResolvedRole = getHotelResolvedRole(selectedSupplier);
  const hotelRoleFlexible = isHotelRoleFlexible(selectedSupplier);
  const hotelService = getHotelService(selectedSupplier);

  const [selectedHotel, setSelectedHotel] = useState(() => {
    const id = initialData?.hotelId || form.hotelId;
    if (!id) return null;
    return {
      id,
      hotelCode: form.hotelCode || initialData?.hotelCode || null,
      name: form.hotelName || initialData?.hotelName || "",
      stars: form.hotelStars || initialData?.hotelStars || 0,
      city: form.hotelCity || initialData?.hotelCity || "",
      country: form.hotelCountry || initialData?.hotelCountry || "",
      address: form.hotelAddress || initialData?.hotelAddress || "",
    };
  });

  const rooms = form.rooms || [];

  /* Handlers */
  const updateRoom = (id, updates) => {
    f("rooms", rooms.map((r) => (r.id === id ? { ...r, ...updates } : r)));
  };

  const addRoom = () => {
    f("rooms", [...rooms, newRoom()]);
  };

  const duplicateRoom = (id) => {
    const orig = rooms.find((r) => r.id === id);
    if (!orig) return;
    f("rooms", [...rooms, { ...orig, id: newRoom().id }]);
  };

  const removeRoom = (id) => {
    f("rooms", rooms.filter((r) => r.id !== id));
  };

  return (
    <>
      {/* ── TRANSFER INCLUSION FLAG ──
         Marks whether this hotel already includes transfer service. When
         unchecked, adding a Transfer to the booking surfaces a warning. */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12, background: "#ffffff", border: "1.5px solid #e5e7eb", borderRadius: 12, padding: "16px 20px", boxShadow: "0 2px 8px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.02)" }}>
        <label style={{ display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer", margin: 0 }}>
          <input
            type="checkbox"
            checked={form.hotelContainsTransfer !== false}
            onChange={(e) => f("hotelContainsTransfer", e.target.checked)}
            style={{ width: 18, height: 18, marginTop: 1, accentColor: "#f5a51e", cursor: "pointer", flexShrink: 0 }}
          />
          <span style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>Does this hotel contain transfer?</span>
            <span style={{ fontSize: 11.5, color: "#6b7280", lineHeight: 1.5 }}>
              Leave ticked if a transfer can be booked with this hotel. Untick it and adding a transfer to this booking will warn that the hotel does not include transfer.
            </span>
          </span>
        </label>
      </div>

      {/* ── ACCOMMODATION CONFIG ── */}
      <div className={s.formCard}>
        <div className={s.formCardHeader}>
          <div className={s.formCardIcon} style={{ background: "#fef3c7", color: "#d97706" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          </div>
          <span className={s.formCardTitle}>Supplier & Configuration</span>
        </div>
        <div className={s.mfRow2}>
          <div className={s.field}><label className={s.fieldLabel}>Select Supplier <span className={s.req}>*</span></label><select className={s.input} value={form.supplierId || ""} onChange={(e) => { const id = e.target.value ? Number(e.target.value) : null; const sup = supplierOptions.find((sv) => String(sv.id) === e.target.value); f("supplierId", id); f("supplier", sup?.name || ""); f("supplierCommercialRole", sup ? resolveSupplierCommercialRole(sup, "Hotel") : ""); }}><option value="">{loadingSuppliers ? "Loading suppliers..." : "Select Supplier"}</option>{supplierOptions.map((sup) => <option key={sup.id} value={sup.id}>{sup.name}{sup.type ? ` (${sup.type})` : ""}</option>)}</select></div>
          <div className={s.field}><label className={s.fieldLabel}>Supplier Booking Reference</label><input className={s.input} placeholder="Enter reference" value={form.supplierRef} onChange={e=>f("supplierRef",e.target.value.toUpperCase())} style={{ textTransform: "uppercase" }}/></div>
        </div>
        {supplierRefWarn && (
          <div style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "10px 14px", background: "#fffbeb", border: "1px solid #fbbf24", borderRadius: 6, fontSize: 12, color: "#92400e", marginTop: 4 }}>
            <span style={{ fontSize: 16, lineHeight: 1 }}>⚠️</span>
            <span><strong>Duplicate reference found</strong> — this reference already exists in{" "}
              {supplierRefWarn.bookings.map((b, i) => (
                <span key={b.bookingId}>{i > 0 ? ", " : ""}<strong>{b.bookingReference}</strong> ({b.status})</span>
              ))}. You can still continue.
            </span>
          </div>
        )}
        {form.supplierId && (
          <div className={s.mfRow2}>
            <div className={s.field}>
              <label className={s.fieldLabel}>Supplier Commercial Role</label>
              {hotelRoleFlexible ? (
                // Hotel service is configured as BOTH — let the user choose.
                <select
                  className={s.input}
                  value={form.supplierCommercialRole || hotelResolvedRole}
                  onChange={e => f("supplierCommercialRole", e.target.value)}
                >
                  <option value="MERCHANT">Merchant (own risk)</option>
                  <option value="AGENT">Agent (intermediary)</option>
                </select>
              ) : (
                // Locked role coming from the supplier's hotel service (or supplier-level fallback).
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", border: "1.5px solid #e5e7eb", borderRadius: 8, background: "#f9fafb" }}>
                  <span style={{
                    display: "inline-flex", alignItems: "center", padding: "3px 10px", borderRadius: 999,
                    fontSize: 12, fontWeight: 700,
                    background: hotelResolvedRole === "MERCHANT" ? "#fef3c7" : "#dbeafe",
                    color: hotelResolvedRole === "MERCHANT" ? "#92400e" : "#1e40af",
                  }}>
                    {hotelResolvedRole === "MERCHANT" ? "Merchant (own risk)" : "Agent (intermediary)"}
                  </span>
                  <span style={{ fontSize: 11, color: "#9ca3af" }}>
                    {hotelService?.commercialRole === "DEFAULT" || !hotelService?.commercialRole
                      ? "Inherited from supplier"
                      : "Fixed by supplier configuration"}
                  </span>
                </div>
              )}
            </div>
            <div />
          </div>
        )}
        <div className={s.mfRow2}>
          <div className={s.field}><label className={s.fieldLabel}>Creation Date</label><input className={s.input} type="date" value={form.creationDate} onChange={e=>f("creationDate",e.target.value)}/></div>
          <div className={s.field}><label className={s.fieldLabel}>Product Status</label><select className={s.input} value={form.status} onChange={e=>f("status",e.target.value)}><option value="">Select Status</option>{PRODUCT_STATUSES.map(st=><option key={st}>{st}</option>)}</select></div>
        </div>
        <div className={s.mfRow2}>
          <div className={s.field}><label className={s.fieldLabel}>Last Payment deadline (Date)</label><input className={s.input} type="date" value={form.lastPaymentDate||""} onChange={e=>f("lastPaymentDate",e.target.value)}/></div>
          <div className={s.field}><label className={s.fieldLabel}>Last Payment Time</label><input className={s.input} type="time" value={form.lastPaymentTime||""} onChange={e=>f("lastPaymentTime",e.target.value)}/></div>
        </div>
        <div className={s.mfRow2}>
          <div className={s.field}><label className={s.fieldLabel}>Last Free Cancellation Date</label><input className={s.input} type="date" value={form.lastFreeCancellationDate||""} onChange={e=>f("lastFreeCancellationDate",e.target.value)}/></div>
          <div className={s.field}><label className={s.fieldLabel}>Last Free Cancellation Time</label><input className={s.input} type="time" value={form.lastFreeCancellationTime||""} onChange={e=>f("lastFreeCancellationTime",e.target.value)}/></div>
        </div>
        <EBRow dateLabel="Last Date for First EB Payment" valueLabel="First EB Payment % or EUR" dateKey="ebFirst_date" unitKey="ebFirst_unit" valueKey="ebFirst_value" form={form} f={f}/>
        <EBRow dateLabel="Last Date for Second EB Payment" valueLabel="Second EB Payment % or EUR" dateKey="ebSecond_date" unitKey="ebSecond_unit" valueKey="ebSecond_value" form={form} f={f}/>
        <EBRow dateLabel="Last Date for Third EB Payment" valueLabel="Third EB Payment % or EUR" dateKey="ebThird_date" unitKey="ebThird_unit" valueKey="ebThird_value" form={form} f={f}/>
      </div>

      {/* ── HOTEL SELECT ── */}
      <div className={s.formCard}>
        <div className={s.formCardHeader}>
          <div className={s.formCardIcon} style={{ background: "#eff6ff", color: "#2563eb" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
          </div>
          <span className={s.formCardTitle}>Accommodation Information</span>
        </div>
        <HotelSearchField selectedHotel={selectedHotel} onSelect={(hotel)=>{setSelectedHotel(hotel);f("hotelName",hotel.name);f("hotelId",hotel.id);f("hotelCode",hotel.hotelCode||"");f("hotelAddress",hotel.address||"");f("hotelCity",hotel.city||"");f("hotelCountry",hotel.country||"");f("hotelStars",hotel.stars||0);}} onClear={()=>{setSelectedHotel(null);f("hotelName","");f("hotelId",null);f("hotelCode","");f("hotelAddress","");f("hotelCity","");f("hotelCountry","");f("hotelStars",0);}}/>
      </div>

      {/* ── STAY DETAILS ── */}
      <div className={s.formCard}>
        <div className={s.formCardHeader}>
          <div className={s.formCardIcon} style={{ background: "#ecfeff", color: "#0891b2" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          </div>
          <span className={s.formCardTitle}>Stay Details</span>
        </div>
        <div className={s.mfRow2}>
          <div className={s.field}><label className={s.fieldLabel}>Check-In Date</label><input className={s.input} type="date" value={form.checkIn} onChange={e=>{const ci=e.target.value;f("checkIn",ci);if(ci&&form.checkOut){const diff=Math.round((new Date(form.checkOut+"T00:00:00")-new Date(ci+"T00:00:00"))/86400000);if(diff>0){f("nights",diff);f("days",diff+1);}}}}/></div>
          <div className={s.field}><label className={s.fieldLabel}>Check-Out Date</label><input className={s.input} type="date" value={form.checkOut} onChange={e=>{const co=e.target.value;f("checkOut",co);if(form.checkIn&&co){const diff=Math.round((new Date(co+"T00:00:00")-new Date(form.checkIn+"T00:00:00"))/86400000);if(diff>0){f("nights",diff);f("days",diff+1);}}}}/></div>
        </div>
        <div className={s.mfRow2}>
          <div className={s.field}><label className={s.fieldLabel}>Nights</label><Stepper value={form.nights||1} min={1} onChange={v=>f("nights",v)}/></div>
          <div className={s.field}><label className={s.fieldLabel}>Days</label><Stepper value={form.days||1} min={1} onChange={v=>f("days",v)}/></div>
        </div>
      </div>

      {/* ── ROOMS ── */}
      <div className={s.formCard}>
        <div className={s.roomsHeader}>
          <div className={s.hotelSectionTitle} style={{ borderBottom: "none", paddingBottom: 0 }}>
            ROOMS
          </div>
          {rooms.length > 0 && (
            <div className={s.roomsStats}>
              <span className={s.roomsStatPill}>
                <strong>{rooms.length}</strong> room{rooms.length !== 1 ? "s" : ""}
              </span>
            </div>
          )}
        </div>

        {rooms.length === 0 ? (
          /* Empty state */
          <div className={s.roomsEmpty}>
            <div className={s.roomsEmptyIcon}><BedIcon /></div>
            <div className={s.roomsEmptyTitle}>No rooms added yet</div>
            <div className={s.roomsEmptyDesc}>Add the first room to start configuring accommodation details. At least one room is required to save.</div>
            <button type="button" className={s.roomsEmptyBtn} onClick={addRoom}>
              <PlusIcon /> Add First Room
            </button>
          </div>
        ) : (
          <>
            {/* Rooms list */}
            <div className={s.roomsList}>
              {rooms.map((room, idx) => (
                <RoomCard
                  key={room.id}
                  room={room}
                  index={idx}
                  onUpdate={updateRoom}
                  onRemove={removeRoom}
                  onDuplicate={duplicateRoom}
                  canRemove={rooms.length > 1}
                />
              ))}
            </div>

            {/* Add Room button */}
            <button type="button" className={s.addRoomBtn} onClick={addRoom}>
              <PlusIcon /> Add Room
            </button>

            {/* Instructions */}
            <div className={s.roomsInstructions}>
              <div className={s.roomsInstructionsTitle}>Instructions for user</div>
              <ul className={s.roomsInstructionsList}>
                <li>Board Type and Room Type are required and cannot be empty.</li>
                <li>Room Characteristic is optional and can be left blank.</li>
                <li>Not in the list? Type it in the search box and click <strong>“Add …”</strong> to use a custom board, room type, or characteristic.</li>
              </ul>
            </div>
          </>
        )}
      </div>

      {/* ── REMARKS ── */}
      <div className={s.formCard}>
        <div className={s.formCardHeader}>
          <div className={s.formCardIcon} style={{ background: "#f5f3ff", color: "#7c3aed" }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
          </div>
          <span className={s.formCardTitle}>Remarks</span>
        </div>
        <div className={s.field}><label className={s.fieldLabel}>Client Remarks</label><textarea className={`${s.input} ${s.textarea}`} rows={3} placeholder="Enter client remarks" value={form.clientRemarks||""} onChange={e=>f("clientRemarks",e.target.value)}/></div>
        <div className={s.field}><label className={s.fieldLabel}>Supplier Remarks</label><textarea className={`${s.input} ${s.textarea}`} rows={3} placeholder="Enter supplier remarks" value={form.supplierRemarks||""} onChange={e=>f("supplierRemarks",e.target.value)}/></div>
        <div className={s.field}><label className={s.fieldLabel}>Office Notes</label><textarea className={`${s.input} ${s.textarea}`} rows={3} placeholder="Enter office notes" value={form.officeNotes||""} onChange={e=>f("officeNotes",e.target.value)}/></div>
      </div>
    </>
  );
};

export default HotelForm;